import { useEffect } from 'react';
import { useEditMode, usePage } from '@vibesites/runtime/react';
import { normalizePath } from './_navShared';

// ============================================================================
// Temporary Floor Plans → Contact Us redirect
// ----------------------------------------------------------------------------
// The Floor Plans page is waiting on its ApartmentSync (Aptsync) widget, so
// every link to it (theme CTAs, editor-entered URLs, richtext links) and any
// direct visit is sent to Contact Us instead. Set FLOOR_PLANS_REDIRECT_ENABLED
// to false once Aptsync is live on /floor-plans.
// ============================================================================

export const FLOOR_PLANS_REDIRECT_ENABLED = true;

const FROM_PATH = '/floor-plans';
const TO_PATH = '/contact-us';

function isFloorPlansHref(href: string): boolean {
  try {
    const url = new URL(href, window.location.href);
    return url.origin === window.location.origin && normalizePath(url.pathname) === FROM_PATH;
  } catch {
    return false;
  }
}

function rewriteAnchor(anchor: Element): void {
  if (isFloorPlansHref(anchor.getAttribute('href') || '')) {
    anchor.setAttribute('href', TO_PATH);
  }
}

function rewriteWithin(root: ParentNode): void {
  root.querySelectorAll('a[href]').forEach(rewriteAnchor);
}

export function useFloorPlansRedirect(): void {
  const isEditMode = !!useEditMode()?.isEditMode;
  const pagePath = normalizePath(usePage()?.path || '');

  // Skipped in edit mode and inside the editor/preview iframe so the page
  // stays reachable for editing.
  useEffect(() => {
    if (!FLOOR_PLANS_REDIRECT_ENABLED || isEditMode) return;
    if (pagePath === FROM_PATH && window.self === window.top) {
      window.location.replace(TO_PATH);
    }
  }, [isEditMode, pagePath]);

  // Rewrites hrefs after hydration (SSR markup stays untouched) and keeps them
  // rewritten as sections re-render or mount late (live collections, editor
  // value changes), so hover, keyboard, and click all resolve to Contact Us.
  useEffect(() => {
    if (!FLOOR_PLANS_REDIRECT_ENABLED) return;

    rewriteWithin(document);

    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type === 'attributes') {
          rewriteAnchor(record.target as Element);
          continue;
        }
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches('a[href]')) rewriteAnchor(node);
          rewriteWithin(node);
        });
      }
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['href'],
    });

    const onInteract = (event: Event) => {
      const anchor = (event.target as Element | null)?.closest?.('a[href]');
      if (anchor) rewriteAnchor(anchor);
    };
    const events = ['click', 'auxclick', 'contextmenu'] as const;
    events.forEach((type) => document.addEventListener(type, onInteract, true));

    return () => {
      observer.disconnect();
      events.forEach((type) => document.removeEventListener(type, onInteract, true));
    };
  }, []);
}
